import { Placement } from './events';
import { Nodes, NodeEventTypes, NodeId, Node } from './nodes';

import { QueryMethods } from '../editor';
import { EditorStore, ActionMethodsWithConfig } from '../editor';
import { CoreEventHandlers } from '../events';
import { ElementType, ReactElement } from 'react';
import { PatchListenerAction, QueryCallbacksFor } from '../utils/useMethods';
import { Delete } from '../utils/utilityTypes';

export type Options = {
    onRender: ElementType<{ render: ReactElement }>;
    onBeforeMoveEnd: (targetNode: Node, newParentNode: Node, existingParentNode: Node) => void;
    onNodesChange: (query: QueryCallbacksFor<typeof QueryMethods>) => void;
    resolver: Resolver;
    enabled: boolean;
    mode: DesignMode;
    indicator: Partial<{
        success: string;
        error: string;
        transition: string;
        thickness: number;
    }>;
    handlers: (store: EditorStore) => CoreEventHandlers | null;
    normalizeNodes: (
        state: EditorState,
        previousState: EditorState,
        actionPerformed: Delete<PatchListenerAction<EditorState, typeof ActionMethodsWithConfig>, 'patches'>,
        query: QueryCallbacksFor<typeof QueryMethods>,
    ) => void;
};

export type Resolver = Record<string, ElementType>;

export interface Indicator {
    placement: Placement;
    error: string | null;
}

export type EditorEvents = Record<NodeEventTypes, Set<NodeId>>;

export type ColorSetting = {
    backgroundImage?: string;
    backgroundStyle?: string;
    color?: string;
};

export type FontSetting = {
    fontFamily: string;
};

export type DesignSettings = {
    coverImage: string;
    colors: Record<string, ColorSetting>;
    fonts: Record<'primary' | 'secondary', FontSetting>;
    styles: {
        icon: IconStyle;
        corner: CornerStyle;
        button: ButtonStyle;
    };
    url: string;
};

export interface OpeningHour {
    id?: number;
    dayOfWeek: number;
    times: {
        start: number;
        end: number;
    }[];
    allDay: boolean;
}

export interface Cuisine {
    id: number;
    name: string;
}

export interface Account {
    id: number;
    name: string;
    username: string;
    about: string | null;
    address: string | null;
    customAddress: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    bookingUrl: string | null;
    socialLinks: SocialLink[];
    openingHours: OpeningHour[];
    cuisineType: Cuisine[];
    orderLinks: AccountLink[];
    logo?: string | null;
    logoList?: string[] | null;
    latitude: number | null;
    longitude: number | null;
    note: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    countryCode: string | null;
    timezone: string | null;
    postcode: string | null;
    planExpiredAt?: Date | null;
    planCode?: string | null;
    webSetting: AccountWebSetting | null;
}
export interface AccountLink {
    id?: number;
    type: string;
    link: string;
    label?: null | string;
}

export type SocialLink = AccountLink;

export type IconStyle = 'outline' | 'solid' | 'shape';

export type CornerStyle = {
    borderRadius: string;
};

export type ButtonStyle = 'solid' | 'outline' | 'text-only';

export type EditorState = {
    nodes: Nodes;
    events: EditorEvents;
    options: Options;
    handlers: CoreEventHandlers | null;
    indicator: Indicator | null;
};
export type AccountWebSetting = {
    showDefaultBranding: boolean | null;
    enableCustomFavicon: boolean | null;
    customFaviconUrl: string | null;
};

export type Plan = {
    NAME: string;
    CODE: string;
    PRICE: number;
    ORIGIN_PRICE?: number;
    INTERVAL?: PlanInterval;
    INTERVAL_COUNT?: number;
    LIMIT: Record<string, number>;
};

export enum PlanInterval {
    DAY = 'day',
    MONTH = 'month',
}
export enum DesignMode {
    EDIT = 'EDIT',
    PREVIEW = 'PREVIEW',
    LIVE = 'LIVE',
}
